import { LoaderFunction } from 'react-router-dom';

// Types
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';

// Constants
import { formActionNaming } from '../../../../../components/forms/ActionForms/constants';

// GraphQL
import { client } from '../../../../../gql/client';
import { fetchMaterialsCatalog } from '../../../../../gql/services/materialsCatalog';
import { FETCH_SUBCODIFICATIONS } from '../../../../../gql/queries/actions/subCodification';
import { FETCH_CODIFICATIONS } from '../../../../../gql/queries/actions/codification';
import { FETCH_MEASURE_TYPES } from '../../../../../gql/queries/actions/measureTypes';
import { FETCH_TEAMS } from '../../../../../gql/queries/users';
import { GET_ORDER_BY_ID } from '../../../../../gql/queries/orders/orders';

// Utils
import { buildWizardMaterialRow } from '../../../../../utils/actionMaterials';
import {
  scopeMaterialsForWizard,
  collectSelectedMaterialIds,
  getCurrentUserTeamIds,
} from '../../../../../utils/materialTeamScope';
import { isBasicAction } from '../../../../../utils/order';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import {
  ACTION_CREATION_SCOPE,
  ACTION_MEDIA_SCOPE,
  readCreationDraftId,
} from '../../../../../utils/draftScopes';
import { foreignCreationParentRedirect } from '../creationDraft';

type LoaderMaterial = { id: string; teamIDs?: string[] };

/**
 * Materiales por equipo — se aplica en TODA actuación, básica o normal.
 *
 * El material se restringe a equipos desde Gestión, y quien registra en campo
 * pertenece a un equipo: un operario de ÈXSER no debe ver material reservado a
 * TRAM 1 aunque esté rellenando una actuación normal, porque las normales las
 * hace también la gente de tablet. Antes esto solo filtraba la básica del RV y
 * la normal salía por un early-return con el catálogo entero.
 *
 * Dos criterios, cada uno aplicable solo si hay con qué (ver
 * `scopeMaterialsForWizard`):
 *
 *  - equipos del USUARIO logueado: en TODA actuación. Los supervisores (coex,
 *    capataz, room-team, super-admin) no tienen equipo, así que para ellos no
 *    aplica y siguen viendo el catálogo completo.
 *  - equipos de la OT padre: SOLO cuando la padre es un RV, o sea en la
 *    básica. Es lo que ya filtraba desde 2026-07-31 y no se amplía a las
 *    normales a propósito: ahí restringiría el catálogo de un supervisor por el
 *    equipo de la OT, que nadie ha pedido.
 *
 * Fail-open deliberado: si no se puede leer la OT padre se filtra igual con lo
 * que se tenga (los equipos del usuario), y si tampoco hay sesión legible se
 * devuelve la lista entera. Antes dejar de más que dejar al operario de campo
 * sin materiales.
 */
const scopeMaterialsToTeams = async <T extends LoaderMaterial>(
  materials: T[],
  orderParentId?: string,
  // Sin tipar como array a propósito: el borrador guarda `materialsTable` como
  // MAPA por id, y asumir array aquí es lo que rompía el filtro (ver
  // `collectSelectedMaterialIds`).
  selectedRows?: unknown
): Promise<T[]> => {
  try {
    const [{ data: teamsData }, parentTeamIds] = await Promise.all([
      client.query({ query: FETCH_TEAMS, fetchPolicy: 'cache-first' }),
      readBasicActionParentTeamIds(orderParentId),
    ]);

    return scopeMaterialsForWizard({
      materials,
      tenantTeamIds: (teamsData?.teams ?? []).map(
        (team: { id: string }) => team.id
      ),
      orderTeamIds: parentTeamIds,
      userTeamIds: getCurrentUserTeamIds(),
      keepIds: collectSelectedMaterialIds(selectedRows),
    });
  } catch (err) {
    console.log('[materials] scope por equipo omitido:', err);
    return materials;
  }
};

/**
 * Equipos de la OT padre **solo si es un Recorregut de Vigilància** — o sea,
 * solo en la actuación básica. Lista vacía si no lo es o no se puede saber, que
 * para el filtro significa «este criterio no aplica», no «no hay equipos».
 */
const readBasicActionParentTeamIds = async (
  orderParentId?: string
): Promise<string[]> => {
  if (!orderParentId) return [];
  try {
    const { data } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: orderParentId },
      fetchPolicy: 'cache-first',
    });
    if (!isBasicAction({ parentOrderType: data?.order?.type })) return [];
    return data?.order?.teamIDs ?? [];
  } catch {
    return [];
  }
};

export const MaterialsLoader: LoaderFunction | undefined = async ({ params, request }) => {
  // Un borrador de OTRO padre no se abre bajo esta URL: atrás, una URL
  // compartida o el «Editar» de un resumen viejo enseñaban — y guardaban — el
  // borrador de la OT Y bajo la de la X (auditoría de creación 2.3).
  const foreignParent = foreignCreationParentRedirect({ params, request });
  if (foreignParent) return foreignParent;
  // Las copias de las pestañas, la lista de fotos y el croquis son del
  // borrador en curso (`draftId`): si eran de otro, fuera, antes de que nadie
  // las lea. Síncrono y antes de cualquier `await`: el loader de `details` y el
  // de la pestaña corren en paralelo (spec 2026-11-02 wizard-draft-ownership).
  const draftId = readCreationDraftId();
  claimDraftScope(ACTION_CREATION_SCOPE, draftId);
  claimDraftScope(ACTION_MEDIA_SCOPE, draftId);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    try {
      // Stringify data
      const stringifyForm = sessionStorage.getItem(
        formActionNaming.NEW_MATERIALS_FORM
      );

      const breadCrumbsDataStringify = sessionStorage.getItem(
        formActionNaming.NEW_ACTION_DATA
      );

      // Variables to assign
      let sessionStorageData;
      let breadCrumbsData;

      // Variables Assignment
      if (stringifyForm) {
        sessionStorageData = JSON.parse(
          decompressSessionStorage(stringifyForm)
        );
      }

      if (breadCrumbsDataStringify) {
        breadCrumbsData = JSON.parse(
          decompressSessionStorage(breadCrumbsDataStringify)
        );
      }
      // Fresco si hay red, de la caché si no: la asignación de equipos se decide
      // en Gestión desde otro dispositivo y la caché persistida tarda hasta 3 h en
      // caducar. Ver `fetchMaterialsCatalog`.
      const materialsUnsorted = await fetchMaterialsCatalog();

      // Load lookup lists — used to resolve names by ID locally (avoids N+1 queries offline)
      const [
        {
          data: { measureTypes },
        },
        {
          data: { codifications },
        },
        {
          data: { subCodifications },
        },
      ] = await Promise.all([
        client.query({ query: FETCH_MEASURE_TYPES }),
        client.query({ query: FETCH_CODIFICATIONS }),
        client.query({ query: FETCH_SUBCODIFICATIONS }),
      ]);

      // Build lookup maps for O(1) resolution
      const measureTypeMap = new Map<string, string>(
        measureTypes.map((m: { id: string; name: string }) => [m.id, m.name])
      );
      const codificationMap = new Map<string, string>(
        codifications.map((c: { id: string; name: string }) => [c.id, c.name])
      );
      const subCodificationMap = new Map<string, string>(
        subCodifications.map((s: { id: string; name: string }) => [
          s.id,
          s.name,
        ])
      );

      // Resolve names locally — no N+1 queries needed
      const materialsData = materialsUnsorted.map(
        (material: {
          id: string;
          materialId: string;
          name: string;
          typeOfMeasurement: string;
          codification: string;
          subCodification: string;
          measureTypeIDs: string[];
          codificationIDs: string[];
          subCodificationIDs: string[];
          teamIDs?: string[];
          isActive?: boolean | null;
        }) =>
          buildWizardMaterialRow(material, {
            typeOfMeasurement:
              measureTypeMap.get(material?.measureTypeIDs?.[0]) || '',
            codification:
              codificationMap.get(material?.codificationIDs?.[0]) || '',
            subCodification:
              subCodificationMap.get(material?.subCodificationIDs?.[0]) || '',
          })
      );
      const sortedMaterials = [...materialsData].sort((a, b) =>
        a.materialId.localeCompare(b.materialId)
      );

      const materials = await scopeMaterialsToTeams(
        sortedMaterials,
        breadCrumbsData?.orderParentId,
        sessionStorageData?.materialsTable
      );

      const combinedData = {
        ...sessionStorageData,
        materialsData: materials,
        breadCrumbsData: {
          orderParentId: breadCrumbsData.orderParentId,
          orderParentRoid: breadCrumbsData.orderParentRoid,
        },
      };

      return combinedData;
    } catch (err) {
      console.log('Loader offline fallback:', err);
      const breadCrumbsDataStringify = sessionStorage.getItem(
        formActionNaming.NEW_ACTION_DATA
      );
      const breadCrumbsData = breadCrumbsDataStringify
        ? JSON.parse(decompressSessionStorage(breadCrumbsDataStringify))
        : {};
      return {
        materialsData: [],
        breadCrumbsData: {
          orderParentId: breadCrumbsData?.orderParentId,
          orderParentRoid: breadCrumbsData?.orderParentRoid,
        },
      };
    }
  };

  return { data: load() };
};
